// ============================================================================
// todo-client-mod -- TodoEditor
//
// 创建/编辑 Todo 的弹窗表单组件。
// 字段：标题、描述、分组、优先级、状态、截止日期、标签
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { useCreateTodo } from '../hooks/use-create-todo.js';
import { useUpdateTodo } from '../hooks/use-update-todo.js';
import { useGroups } from '../hooks/use-groups.js';
import { PlusIcon } from './icons.js';
import { radius, typography, transition, priorityColors } from '../styles/theme.js';
const GROUP_ICON_MAP = {
    inbox: '📥', work: '💼', personal: '👤', shopping: '🛒', study: '📚',
    health: '❤', travel: '✈', finance: '💰', idea: '💡', star: '★', folder: '📁',
};
const PRIORITY_LABELS = { low: '低', medium: '中', high: '高', urgent: '紧急' };
const PRIORITY_LIST = ['low', 'medium', 'high', 'urgent'];
export function TodoEditor({ todo, defaultGroupId, onSuccess, onCancel }) {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const isEditMode = !!todo;
    const { groups, loading: groupsLoading } = useGroups();
    const { creating, createTodo } = useCreateTodo();
    const { updating, updateTodo } = useUpdateTodo();
    const [title, setTitle] = React.useState(todo?.title ?? '');
    const [description, setDescription] = React.useState(todo?.description ?? '');
    const [showDescription, setShowDescription] = React.useState(!!todo?.description);
    const [groupId, setGroupId] = React.useState(todo?.groupId ?? defaultGroupId ?? '');
    const [priority, setPriority] = React.useState(todo?.priority ?? 'medium');
    const [status, setStatus] = React.useState(todo?.status ?? 'pending');
    const [dueDateStr, setDueDateStr] = React.useState(todo?.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 10) : '');
    const [tagInput, setTagInput] = React.useState('');
    const [tags, setTags] = React.useState(todo?.tags ?? []);
    const [error, setError] = React.useState(null);
    const isSubmitting = creating || updating;
    const inputStyle = {
        padding: '8px 12px',
        border: `1px solid ${theme.border.default}`,
        borderRadius: radius.md,
        fontSize: typography.fontSize.base,
        outline: 'none',
        backgroundColor: theme.bg.secondary,
        color: theme.text.primary,
        transition: `border-color ${transition.fast}, box-shadow ${transition.fast}, background-color ${transition.fast}`,
    };
    const handleAddTag = () => {
        const tag = tagInput.trim();
        if (tag && !tags.includes(tag) && tags.length < 10) {
            setTags([...tags, tag]);
            setTagInput('');
        }
    };
    const handleRemoveTag = (tag) => setTags(tags.filter(t => t !== tag));
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!title.trim()) {
            setError('请输入待办标题');
            return;
        }
        // 校验 groupId 是否仍然有效（分组可能已被删除或数据库已重置）
        const validGroupId = groupId && groups.some(g => g._id === groupId) ? groupId : undefined;
        const params = {
            title: title.trim(),
            description: description.trim() || undefined,
            groupId: validGroupId,
            priority,
            status,
            dueDate: dueDateStr ? new Date(dueDateStr).getTime() : undefined,
            tags: tags.length > 0 ? tags : undefined,
        };
        const result = isEditMode && todo ? await updateTodo(todo._id, params) : await createTodo(params);
        if (result) {
            onSuccess?.(result);
        }
        else {
            setError(isEditMode ? '更新失败' : '创建失败');
        }
    };
    const overlayStyle = {
        position: 'fixed', inset: 0,
        backgroundColor: theme.bg.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: isMobile ? '12px' : '20px',
    };
    const modalStyle = {
        backgroundColor: theme.bg.elevated, borderRadius: radius.xl, padding: isMobile ? '20px' : '24px',
        width: '480px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: theme.shadow.xl,
        animation: 'todo-scale-in 0.2s ease forwards',
    };
    return React.createElement('div', { style: overlayStyle }, React.createElement('div', { style: modalStyle, onClick: (e) => e.stopPropagation() }, React.createElement('h3', { style: { margin: '0 0 20px 0', fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary } }, isEditMode ? '编辑待办事项' : '新建待办事项'), React.createElement('form', { onSubmit: handleSubmit, style: { display: 'flex', flexDirection: 'column', gap: '12px' } }, 
    // 标题
    React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '标题 *'), React.createElement('input', {
        type: 'text', value: title,
        onChange: (e) => setTitle(e.target.value),
        placeholder: '请输入待办标题...', style: inputStyle, autoFocus: true,
    }), 
    // 描述
    showDescription
        ? React.createElement('div', null, React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '描述'), React.createElement('textarea', {
            value: description,
            onChange: (e) => setDescription(e.target.value),
            placeholder: '补充详细信息（可选）...', style: { ...inputStyle, resize: 'vertical' }, rows: 3,
        }))
        : React.createElement('button', {
            type: 'button', onClick: () => setShowDescription(true),
            style: {
                display: 'inline-flex', alignItems: 'center', fontSize: typography.fontSize.sm,
                color: theme.status.primary, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                transition: `color ${transition.fast}`,
            },
        }, React.createElement(PlusIcon), React.createElement('span', { style: { marginLeft: '4px' } }, '添加描述')), 
    // 分组
    groupsLoading
        ? React.createElement('div', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary, padding: '8px 0' } }, '加载分组中...')
        : React.createElement('div', null, React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '分组'), React.createElement('select', {
            value: groupId, onChange: (e) => setGroupId(e.target.value),
            style: inputStyle,
        }, React.createElement('option', { value: '' }, '无分组'), ...groups.map(g => React.createElement('option', { key: g._id, value: g._id }, `${GROUP_ICON_MAP[g.icon] ?? '📁'}  ${g.name}`)))), 
    // 优先级
    React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '优先级'), React.createElement('div', { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' } }, ...PRIORITY_LIST.map(p => React.createElement('label', {
        key: p, style: {
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: typography.fontSize.base,
            cursor: 'pointer', padding: '6px 12px', borderRadius: radius.md,
            border: `1px solid ${priority === p ? theme.status.primary : theme.border.default}`,
            backgroundColor: priority === p ? theme.interaction.selected : 'transparent',
            transition: `background-color ${transition.fast}, border-color ${transition.fast}`,
        },
    }, React.createElement('input', { type: 'radio', name: 'priority', value: p, checked: priority === p, onChange: () => setPriority(p), style: { display: 'none' } }), React.createElement('span', { style: { color: priorityColors[p], fontWeight: typography.fontWeight.medium } }, PRIORITY_LABELS[p])))), 
    // 状态
    React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '状态'), React.createElement('select', {
        value: status, onChange: (e) => setStatus(e.target.value),
        style: inputStyle,
    }, React.createElement('option', { value: 'pending' }, '待处理'), React.createElement('option', { value: 'in_progress' }, '进行中'), React.createElement('option', { value: 'completed' }, '已完成'), React.createElement('option', { value: 'cancelled' }, '已取消')), 
    // 截止日期
    React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '截止日期'), React.createElement('input', {
        type: 'date', value: dueDateStr,
        onChange: (e) => setDueDateStr(e.target.value),
        style: inputStyle,
    }), 
    // 标签
    React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, `标签 (${tags.length}/10)`), React.createElement('div', { style: { display: 'flex', gap: '8px' } }, React.createElement('input', {
        type: 'text', value: tagInput,
        onChange: (e) => setTagInput(e.target.value),
        onKeyDown: (e) => { if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        } },
        placeholder: '输入标签后回车', style: { ...inputStyle, flex: 1 },
    }), React.createElement('button', { type: 'button', onClick: handleAddTag, style: {
            padding: '6px 12px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
            backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.sm, cursor: 'pointer',
            color: theme.text.primary, transition: `background-color ${transition.fast}`,
        } }, '添加')), tags.length > 0
        ? React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '6px' } }, ...tags.map(tag => React.createElement('span', { key: tag, style: {
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
                backgroundColor: theme.interaction.selected, color: theme.status.primary,
                borderRadius: radius.sm, fontSize: typography.fontSize.sm,
            } }, tag, React.createElement('button', { type: 'button', onClick: () => handleRemoveTag(tag), style: {
                background: 'none', border: 'none', color: theme.status.primary, fontSize: '16px',
                cursor: 'pointer', padding: 0, lineHeight: 1,
            } }, '×'))))
        : null, 
    // 错误
    error ? React.createElement('div', {
        style: {
            padding: '10px', backgroundColor: theme.status.dangerBg,
            border: `1px solid ${theme.status.danger}40`, borderRadius: radius.md,
            fontSize: typography.fontSize.sm, color: theme.status.danger,
            animation: 'todo-shake 0.4s ease',
        },
    }, error) : null, 
    // 底部按钮
    React.createElement('div', {
        style: {
            display: 'flex', justifyContent: 'flex-end', gap: '8px',
            marginTop: '8px', paddingTop: '12px', borderTop: `1px solid ${theme.border.divider}`,
        },
    }, React.createElement('button', {
        type: 'button', onClick: onCancel, disabled: isSubmitting,
        style: {
            padding: '8px 16px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
            backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.base, color: theme.text.secondary,
            cursor: 'pointer', transition: `background-color ${transition.fast}`,
        },
    }, '取消'), React.createElement('button', {
        type: 'submit', disabled: isSubmitting || !title.trim(),
        style: {
            padding: '8px 16px', border: 'none', borderRadius: radius.md,
            backgroundColor: theme.status.primary, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
            color: theme.text.inverse, cursor: 'pointer', opacity: (isSubmitting || !title.trim()) ? 0.6 : 1,
            transition: `background-color ${transition.fast}, opacity ${transition.fast}`,
        },
    }, isSubmitting ? '保存中...' : (isEditMode ? '更新' : '创建'))))));
}
//# sourceMappingURL=todo-editor.js.map